import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, FormFeedback, Label } from 'reactstrap';
import { useDropzone } from "react-dropzone";
import { useFormik } from "formik";
import * as Yup from "yup";
import * as XLSX from "xlsx";
import { callUpdateFinancialsSummary } from 'http/fundLync/entityDocuments.helper';
import ToastDialog from 'components/Common/ToastDialog';
import Fundlync_financial_summary_template from './Fundlync_financial_summary_template.xlsx';

const Provisional = ({ entityId, responseId, onUpdate }) => {
    const [uploadProgress, setUploadProgress] = useState(0);
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showButtons, setShowButtons] = useState(false);
    const [excelData, setExcelData] = useState([]);
    const [toastInfo, setToastInfo] = useState({ showToast: false, message: '', role: '' });
    const toggleButtons = () => setShowButtons(!showButtons);
    const [dataByYear, setDataByYear] = useState({});

    const validationFileUpload = useFormik({
        enableReinitialize: true,

        initialValues: {
            fileUpload: null,
        },
        validationSchema: Yup.object({
            fileUpload: Yup.mixed()
            .required("Upload file is required")
                .test("fileType", "Invalid file type. Only images and PDFs are allowed.", (value) => {
                    if (!value) return false;
                        const allowedFileTypes = [
                            'image/jpeg',
                            'image/png',
                            'application/pdf',
                            'image/jpg',
                            'application/vnd.ms-excel',
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        ];
                        return allowedFileTypes.includes(value.type);
                    }),
                }),
        onSubmit: (values) => {
            saveData(values);
        }
    });

    const saveData = async () => {
        const yearData = Object.keys(dataByYear).map(year => ({
            year,
            financials: dataByYear[year].map(item => ({
                particulars: item.particulars,
                amount: item.amount,
            })),
            isProvisional: year.includes("Provisional")
        }));

        const dataToSend = {
            entityId: entityId,
            responseId: responseId,
            updatedData: yearData,
        };

        try {
            setLoading(true);
            const response = await callUpdateFinancialsSummary(dataToSend);
            if (response.code === 200 && response.status) {
                setToastInfo({
                    message: response.message || "file uploaded successfully",
                    showToast: true,
                    role: "success",
                });
                onUpdate()
                
            } else {
                setToastInfo({
                    message: response.message || "Invalid file",
                    showToast: true,
                    role: "error",
                });
            }
        } catch (error) {
            setToastInfo({
                message: error.response?.data?.message || error.message || "An unknown error occurred.",
                showToast: true,
                role: "error",
            });
        } finally {
            setLoading(false);
        }
    };



    const onDrop = useCallback((acceptedFiles) => {
        const selectedFile = acceptedFiles[0];
        if (selectedFile) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const data = e.target.result;
                const workbook = XLSX.read(data, { type: "binary" });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const sheetData = XLSX.utils.sheet_to_json(sheet);

                setExcelData(sheetData);
                processExcelData(sheetData);

                validationFileUpload.setFieldValue(
                    "fileUpload",
                    selectedFile, true
                );
            };
            reader.readAsBinaryString(selectedFile);
        }
    }, [validationFileUpload]);

    const processExcelData = (data) => {
        const newDataByYear = {};

        data.forEach((row) => {
            const particulars = row["Particulars (Amount In Cr.)"];
            const years = Object.keys(row).filter(key => key !== "Particulars (Amount In Cr.)");

            years.forEach(year => {
                if (!newDataByYear[year]) {
                    newDataByYear[year] = [];
                }
                newDataByYear[year].push({
                    particulars,
                    amount: cleanAmount(row[year]),
                });
            });
        });

        setDataByYear(newDataByYear);
    };

    const cleanAmount = (amount) => {
        if (amount === "#DIV/0!" || amount === undefined || amount === "") {
            return 0;
        }
        return parseFloat(amount) || 0;
    };

    useEffect(() => {
        if (excelData.length > 0) {
        }
    }, [excelData]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
    });

    function downloadExcelFile() {
        const link = document.createElement("a");
        link.href = Fundlync_financial_summary_template
        link.download = "Fundlync_financial_summary_template.xlsx"
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    return (
        <div>
            <ToastDialog
                message={typeof toastInfo.message === "string" ? toastInfo.message : JSON.stringify(toastInfo.message)}
                showToast={toastInfo.showToast}
                role={toastInfo.role}
                onCloseFn={() => setToastInfo({ showToast: false })}
            />

            <div className="d-flex flex-column mb-2">
                <div className="d-flex justify-content-end align-items-center gap-3">
                    <Button color="success" onClick={toggleButtons}>
                        + Add Provisional
                    </Button>
                    <Button
                        type="button"
                        color="light"
                        className="border-success d-flex align-items-center text-grey"
                        style={{ fontWeight: "500" }}
                        onClick={downloadExcelFile}
                    >
                        <i className="mdi mdi-download me-1"></i> Download
                    </Button>
                </div>

                {showButtons && (
                    <div className="row d-flex align-items-center justify-content-end">
                        <Col sm="12"></Col>
                        <Col sm="12" md="6">
                            <div className="d-flex flex-column">
                                <Label htmlFor="horizontal-email-Input" className="col-form-label">
                                    Upload File{" "}
                                    <span className="text-primary">
                                        (allowed formats: .xlsx, .xls, .jpg, .png, .jpeg, .pdf)
                                    </span>
                                    <br />
                                    <small className="text-danger">
                                        *The maximum file size allowed is 10 MB
                                    </small>
                                </Label>

                                <div
                                    className="drop"
                                    onClick={() => {
                                        validationFileUpload.setFieldTouched("fileUpload", true);
                                        setErrorMessage(null);
                                        setUploadProgress({});
                                    }}
                                >
                                    <div {...getRootProps()} className="text-wrapper">
                                        <div className="centered">
                                            <input
                                                type="file"
                                                name="formatDocument"
                                                id="itrFileExcel"
                                                accept=".jpg, .png, .jpeg, .pdf, .xlsx, .xls"
                                                {...getInputProps({
                                                    onClick: () => {
                                                        setErrorMessage(null);
                                                        setUploadProgress({});
                                                    },
                                                })}
                                            />
                                            <img
                                                src="https://scfuat.cashinvoice.in/frontend/assets/themes/image/icon/cloud.png"
                                                className="img-fluid"
                                                style={{ width: "30px" }}
                                                alt="Upload"
                                            />
                                            {isDragActive && <p className="textLink">Drop here</p>}
                                            <span className="textLink">Browse files to attach</span> or Drop
                                        </div>
                                    </div>
                                </div>

                                {validationFileUpload.values.fileUpload && (
                                    <div className="mt-2">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span>{validationFileUpload.values.fileUpload.name}</span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                style={{
                                                    border: "none",
                                                    borderRadius: "50%",
                                                    fontWeight: "800",
                                                }}
                                                onClick={() => {
                                                    validationFileUpload.setFieldValue("fileUpload", null);
                                                }}
                                            >
                                                X
                                            </button>
                                        </div>

                                        {uploadProgress[validationFileUpload.values.fileUpload.name] > 0 &&
                                            !errorMessage && (
                                                <div className="progress mt-2">
                                                    <div
                                                        className="progress-bar bg-success"
                                                        role="progressbar"
                                                        style={{
                                                            width: `${uploadProgress[validationFileUpload.values.fileUpload.name]}%`,
                                                        }}
                                                        aria-valuenow={uploadProgress[validationFileUpload.values.fileUpload.name]}
                                                        aria-valuemin="0"
                                                        aria-valuemax="100"
                                                    >
                                                        {`${uploadProgress[validationFileUpload.values.fileUpload.name]}%`}
                                                    </div>
                                                </div>
                                            )}

                                        {errorMessage && (
                                            <div className="alert alert-danger mt-2">
                                                {errorMessage}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {validationFileUpload.touched.fileUpload &&
                                    validationFileUpload.errors.fileUpload && (
                                        <FormFeedback
                                            type="invalid"
                                            style={{ display: "block" }}
                                        >
                                            {validationFileUpload.errors.fileUpload}
                                        </FormFeedback>
                                    )}
                            </div>
                            <hr />
                        </Col>
                        <Col sm="12"></Col>
                        <Col
                            sm="12"
                            md="6"
                            className="d-flex justify-content-end mb-4"
                        >
                            <button
                                type="submit"
                                className="btn btn-success save-customer"
                                disabled={loading}
                                onClick={validationFileUpload.handleSubmit}
                            >
                                {loading && (
                                    <span
                                        className="spinner-border spinner-border-sm ms-2 me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                )}
                                {loading && <span>Saving</span>}
                                {!loading && <span>Save</span>}
                            </button>
                        </Col>
                    </div>
                )}
            </div>
        </div>
    );
}    

export default Provisional;